<script setup lang="ts">
const modalsStore = useModalsStore();
const props = withDefaults(
    defineProps<{
        item: {
            title: string,
            desc?: string,
            chip?: string,
            img?: string,
            boldCard?: {
                type: 'popular' | 'suggest',
                title: string,
            },
            amounts: Array<{
                title: string,
                value: Array<{
                    min?: number,
                    max?: number,
                    price: string,
                }> | string,
                unit: string,
                info?: string[],
                isRange?: boolean,
            }>,
            features?: Array<{
                title: string,
                value: boolean,
                isBoldText?: boolean
            }>,
        },
        primaryBtn?: boolean,
        shoppingPath?: string,
    }>(),
    {
        shoppingPath: '/online-shopping/buy/'
    }
);

export type TPricing = typeof props.item;

const isPrimaryBtn = computed((): boolean => 
    props.primaryBtn || (props.item?.boldCard?.type === 'popular')
);

function getAmountParts(amount: TPricing['amounts'][number]) {
    if (amount.isRange && Array.isArray(amount.value)) {
        return [
            { label: 'از', price: amount.value[0]?.price },
            { label: 'تا', price: amount.value[1]?.price },
        ];
    }
    return [{ label: null, price: amount.value as string }];
}

function handleOpenModal() {
    modalsStore.openModal('CTA');
};
</script>

<template>
    <div 
        class="w-full max-w-90 border border-gray-200 p-1 shadow-gray-m bg-primary-50 rounded-[20px]
            transition-all duration-300 hover:border-primary-300 hover:shadow-primary-xl relative flex-center"
        :class="[
            (props.item?.boldCard?.type === 'popular') && 'popular-card',
            (props.item?.boldCard?.type === 'suggest') && 'suggest-card',
        ]"
        :data-label="props.item?.boldCard?.title"
    >
        <div 
            class="w-full bg-white rounded-2xl py-6 relative"
            :class="[
                (props.item?.boldCard?.type === 'popular') && 'bg-linear-to-b from-primary-100 to-white',
            ]"
        >
            <div class="w-full flex items-center justify-between pl-5">
                <div class="w-fit py-1.5 px-3 bg-secondary-500 rounded-bl-2xl rounded-tl-[5px]">
                    <span class="text-body1-bold">{{ props.item.title }}</span>
                </div>
                <NuxtImg
                    v-if="props.item?.img"
                    :src="props.item.img"
                    :alt="props.item.title"
                    loading="lazy"
                    class="w-6.5 drop-shadow-primary-200 drop-shadow-sm"
                />
            </div>
            <div class="w-full flex flex-col gap-8 mt-10 px-5">
                <p v-if="props.item.desc" class="text-body2-semibold">{{ props.item.desc }}</p>

                <div class="w-full flex flex-col items-center gap-8">
                    <div 
                        v-for="amount, index in props.item.amounts"
                        :key="index"
                        class="w-full flex flex-col items-center gap-2"
                    >
                        <div class="flex-center gap-1 flex-wrap">
                            <template v-for="part, index in getAmountParts(amount)" :key="index">
                                <span v-if="part.label">{{ part.label }}</span>
                                <span class="h3">{{ part.price }}</span>
                            </template>
                            {{ amount.unit }}

                            <template v-if="amount.info">
                                <Tooltip position="top">
                                    <template #activator>
                                        <Icon 
                                            name="solar:info-circle-linear" 
                                            size="20" 
                                            class="text-primary-600" 
                                        />
                                    </template>
                                    <div
                                        v-for="item, index in amount.info"
                                        :key="item"
                                        class="w-50 lg:w-75 relative p-3! text-caption text-gray-500 text-start! z-1"
                                        :class="index % 2 ? 'bg-gray-100' : 'bg-white'"
                                    >
                                        {{ item }}
                                    </div>
                                </Tooltip>
                            </template>
                        </div>
                        <p v-if="amount.title" class="text-caption text-gray-400">{{ amount.title }}</p>
                    </div>
                </div>

                <ChipsBase
                    v-if="props.item.chip"
                    size="Small" 
                    variant="purple" 
                    class="mx-auto"
                >
                    {{ props.item.chip }}
                </ChipsBase>

                <Button 
                    :type="isPrimaryBtn ? 'Primary' : 'Secondary'"
                    pill
                    :to="props.shoppingPath"
                    link
                >
                    خرید آنلاین
                </Button>
                
                <Button 
                    type="Outline" 
                    @click="handleOpenModal"
                    class="-mt-2"
                    pill
                >
                    دریافت مشاوره
                </Button>

                <div v-if="props.item.features" class="flex flex-col gap-4">
                    <div 
                        v-for="feature in props.item.features"
                        :key="feature.title"
                        class="flex items-center gap-2"
                    >
                        <Icon 
                            :name="feature.value ? 'solar:check-circle-bold' : 'solar:close-circle-bold'" 
                            :class="feature.value ? 'text-success-500' : 'text-error-700'"
                            size="17" 
                            class="min-w-4.25"
                        />
                        <span 
                            class="text-body2 text-gray-500 text-start"
                            :class="feature.isBoldText && 'text-gray-600! font-bold!'"
                        >
                            {{ feature.title }}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
@reference "~/assets/css/main.css";

.popular-card {
    @apply
    bg-white border-primary-300 mt-8 lg:mt-0
}
.popular-card::before {
    content: attr(data-label);
    @apply
    absolute w-[75%] py-1.5 bg-primary-500 rounded-t-[20px] text-white flex 
    justify-center items-center text-[14px] font-bold -top-8.25
}

.suggest-card {
    @apply
    bg-secondary-50/50 border-secondary-500 mt-8 lg:mt-0
}
.suggest-card::before {
    content: attr(data-label);
    @apply
    absolute w-[75%] py-1.5 bg-secondary-500 rounded-t-[20px] flex 
    justify-center items-center text-[14px] font-bold -top-8.25
}
</style>